// @flow

import React from 'react';
import styled from 'styled-components';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, getCompanyCurrency } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 32px;
`;

const KPICard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const KPILabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const KPIValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ color }) => color || '#333'};
`;

const KPISubtext = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  padding: 0 24px;
  margin: 24px 0 16px;
`;

const AccountsList = styled.div`
  padding: 0 24px;
`;

const AccountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
  &:last-child {
    border-bottom: none;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AccountNumber = styled.span`
  font-size: 13px;
  color: #999;
  font-family: 'SF Mono', 'Menlo', monospace;
`;

const AccountName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const AccountBalance = styled.span`
  font-size: 14px;
  font-weight: 600;
  font-family: 'SF Mono', 'Menlo', monospace;
  color: ${({ value }) => (value < 0 ? '#C62828' : '#2E7D32')};
`;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCompanyCurrency(),
  }).format(value);

const FinanceDashboard = () => {
  return (
    <QueryRenderer
      environment={relayEnvironment}
      cacheConfig={{ force: true }}
      query={graphql`
        query FinanceDashboardQuery {
          glAccounts(
            first: 200
            isActive: true
            sortBy: { field: ACCOUNT_NUMBER, direction: ASC }
          ) {
            edges {
              node {
                id
                accountNumber
                name
                accountType
                normalBalance
                balance
              }
            }
          }
        }
      `}
      variables={{}}
      render={({ props }) => (
        <Page>
          <PageHeaderList title={i18n.t('Finance Dashboard')} />
          {!props ? (
            <Loader />
          ) : (
            <FinanceDashboardContent accounts={props.glAccounts.edges.map(e => e.node)} />
          )}
        </Page>
      )}
    />
  );
};

type ContentProps = {
  accounts: Array<Object>,
};

const FinanceDashboardContent = ({ accounts }: ContentProps) => {
  // Credit-normal accounts (REVENUE, LIABILITY, EQUITY) are stored as negative
  // in the GL (credits reduce the raw balance). Negate them so they display
  // as positive meaningful figures.
  const getBalanceByType = (type: string) =>
    accounts
      .filter(a => a.accountType === type)
      .reduce((sum, a) => {
        const val = a.normalBalance === 'CREDIT' ? -a.balance : a.balance;
        return sum + val;
      }, 0);

  const totalAssets = getBalanceByType('ASSET');
  const totalLiabilities = getBalanceByType('LIABILITY');
  const totalEquity = getBalanceByType('EQUITY');
  const totalRevenue = getBalanceByType('REVENUE');
  const totalCOGS = getBalanceByType('COGS');
  const totalExpenses = getBalanceByType('EXPENSE');
  const grossProfit = totalRevenue - totalCOGS;
  const netIncome = grossProfit - totalExpenses;

  // Extract key accounts
  const cashAccount = accounts.find(a => a.accountNumber === '1000');
  const arAccount = accounts.find(a => a.accountNumber === '1100');
  const apAccount = accounts.find(a => a.accountNumber === '2000');
  const inventoryAccounts = accounts.filter(a =>
    ['1200', '1250', '1300'].includes(a.accountNumber),
  );

  return (
    <>
      <DashboardGrid>
        <KPICard>
          <KPILabel>{i18n.t('Total Revenue')}</KPILabel>
          <KPIValue color={totalRevenue >= 0 ? '#2E7D32' : '#C62828'}>{formatCurrency(totalRevenue)}</KPIValue>
          <KPISubtext>{i18n.t('From posted journal entries')}</KPISubtext>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Gross Profit')}</KPILabel>
          <KPIValue color={grossProfit >= 0 ? '#2E7D32' : '#C62828'}>
            {formatCurrency(grossProfit)}
          </KPIValue>
          <KPISubtext>{i18n.t('Revenue minus COGS')}</KPISubtext>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Net Income')}</KPILabel>
          <KPIValue color={netIncome >= 0 ? '#2E7D32' : '#C62828'}>
            {formatCurrency(netIncome)}
          </KPIValue>
          <KPISubtext>{i18n.t('Revenue minus all expenses')}</KPISubtext>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Cash Balance')}</KPILabel>
          <KPIValue>{formatCurrency(cashAccount ? cashAccount.balance : 0)}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Accounts Receivable')}</KPILabel>
          <KPIValue>{formatCurrency(arAccount ? arAccount.balance : 0)}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Accounts Payable')}</KPILabel>
          <KPIValue color="#C62828">
            {formatCurrency(apAccount ? apAccount.balance : 0)}
          </KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Total Assets')}</KPILabel>
          <KPIValue>{formatCurrency(totalAssets)}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>{i18n.t('Total Liabilities')}</KPILabel>
          <KPIValue color="#C62828">{formatCurrency(totalLiabilities)}</KPIValue>
        </KPICard>
      </DashboardGrid>

      <SectionTitle>{i18n.t('Inventory Accounts')}</SectionTitle>
      <AccountsList>
        {inventoryAccounts.map(account => (
          <AccountRow key={account.id}>
            <AccountInfo>
              <AccountNumber>{account.accountNumber}</AccountNumber>
              <AccountName>{account.name}</AccountName>
            </AccountInfo>
            <AccountBalance value={account.balance}>
              {formatCurrency(account.balance)}
            </AccountBalance>
          </AccountRow>
        ))}
      </AccountsList>

      <SectionTitle>{i18n.t('Expense Breakdown')}</SectionTitle>
      <AccountsList>
        {accounts
          .filter(a => a.accountType === 'COGS' || a.accountType === 'EXPENSE')
          .map(account => (
            <AccountRow key={account.id}>
              <AccountInfo>
                <AccountNumber>{account.accountNumber}</AccountNumber>
                <AccountName>{account.name}</AccountName>
              </AccountInfo>
              <AccountBalance value={account.balance}>
                {formatCurrency(account.balance)}
              </AccountBalance>
            </AccountRow>
          ))}
      </AccountsList>
    </>
  );
};

export default FinanceDashboard;
